import environment from '../environment';
import { pinBuilder } from '../core/map/pinBuilder';
import Promise from "bluebird";
import templates from '../core/templates';
import Leaflet from './Leaflet';
import ColorBrewer from '../core/tools/colorbrewer';
import { LayerContextEnum } from '../../enuns/layerContext.enum';
import { WorkshopPartParameter } from '../../constants/workshopPartParameter';
import translate from '../core/tools/translate';

class WorkshopPartLayer {
    constructor(currentMap) {
        const clusterImageWorkshop = new Image();
        clusterImageWorkshop.src = `${environment.get('IMAGES_HOST')}/icon_marker_workshop.png`;

        const onClick = (feature, layer) => { };

        const clusterTemplate = cluster => {
            const data = {
                value: cluster.getAllChildMarkers().length,
            };

            return pinBuilder.create.pin.imageWithValue(data, clusterImageWorkshop);
        };

        const createPopupTemplate = (properties, layer) =>
            new Promise((resolve, reject) => {
                const title = WorkshopPartLayer.getTitle(properties.Parameters);

                properties.document = properties.DocumentNumber
                    ? properties.DocumentNumber.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
                    : undefined;

                properties.documentRoot = properties.DocumentNumber
                    ? properties.DocumentNumber.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3")
                    : undefined;

                properties.ParameterTemplates = properties.Parameters
                    .filter(x => x.Key !== WorkshopPartParameter.FantasyName && x.Key !== WorkshopPartParameter.CompanyName)
                    .map(x => {
                        if (x.Translate && x.Translate.indexOf('Common.') === -1) {
                            return { ...x, Translate: 'Common.' + x.Key };
                        }

                        x.Translate = 'Common.' + x.Key;

                        return x;
                    });

                const $template = $(templates('map-layer-workshop-part-popup', { title, ...properties }));

                resolve($template);
            });

        const createClusterPopupTemplate = features => {
            const properties = {
                workshops: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.Workshop),
                parts: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.AutoParts),
                accioly: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.Accioly),
                acdelco: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.Acdelco),
                motoworkshops: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.MotoWorkShop),
                motoparts: WorkshopPartLayer.getPropertiesByLayerContext(features, LayerContextEnum.MotoParts),
            };

            properties.workshopShow = properties.workshops.length || properties.parts.length;
            properties.motoworkshopShow = properties.motoworkshops.length || properties.motoparts.length;
            properties.acdelcoShow = properties.accioly.length || properties.acdelco.length;

            properties.workshopsTotal = properties.workshops.length; 
            properties.partsTotal = properties.parts.length;
            properties.motoworkshopsTotal = properties.motoworkshops.length; 
            properties.motopartsTotal = properties.motoparts.length;
            properties.acciolyTotal = properties.accioly.length;
            properties.acdelcoTotal = properties.acdelco.length;

            properties.workshops = properties.workshops.slice(0, 5);
            properties.parts = properties.parts.slice(0, 5);
            properties.motoworkshops = properties.motoworkshops.slice(0, 5);
            properties.motoparts = properties.motoparts.slice(0, 5);
            properties.accioly = properties.accioly.slice(0, 5);
            properties.acdelco = properties.acdelco.slice(0, 5);

            properties.wrapWorkshops = properties.workshops.length === 5;
            properties.wrapParts = properties.parts.length === 5;
            properties.wrapMotoWorkshops = properties.motoworkshops.length === 5;
            properties.wrapMotoParts = properties.motoparts.length === 5;
            properties.wrapAccioly = properties.accioly.length === 5;
            properties.wrapAcdelco = properties.acdelco.length === 5;

            return new Promise((resolve, reject) => {
                const template = $(templates('map-layer-workshop-part-cluster-popup', properties));
                resolve(template);
            });
        };

        return new Leaflet.D3Layer({
            map: currentMap,
            id: `spatial_workshop_part_layer`,
            remote: {
                method: 'POST',
                url: `${environment.get('API_HOST')}/Layer/getworkshopparts`,
                params: {}
            },
            scaleRange: [7, 20],
            showAll: false,
            autofocus: false,
            uniqueField: 'Id',
            sort: 'SocialName',
            click: onClick,
            legend: {
                title: translate('Common.WorkshopPart'),
                text: (data) => WorkshopPartLayer.getLegendName[data.LayerContext](),
                icon: (data) => WorkshopPartLayer.getPinIcon[`${data.LayerContext}_xs`]
            },
            popup: {
                template: createPopupTemplate,
                clusterTemplate: createClusterPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, 0),
                    closeButton: false,
                    autoclose: true
                },
                single: true
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    disableAtZoom: 20,
                    template: clusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_GREEN)
                },
                style: {
                    icon: {
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_workshop.png`,
                        getUrl: (data) => WorkshopPartLayer.getPinIcon[`${data.LayerContext}_xs`],
                        iconSize: [12, 12],
                        iconAnchor: [6, 6],
                        labelAnchor: [6, 0]
                    },
                    riseOnHover: true
                }
            }
        });
    }

    static getPinIcon = {
        [LayerContextEnum.Workshop]: `${environment.get('IMAGES_HOST')}/pin_workshop.png`,
        [`${LayerContextEnum.Workshop}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_blue.png`,
        [`${LayerContextEnum.Workshop}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_blue_xs.png`,
        [LayerContextEnum.AutoParts]: `${environment.get('IMAGES_HOST')}/pin_parts.png`,
        [`${LayerContextEnum.AutoParts}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_red.png`,
        [`${LayerContextEnum.AutoParts}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_red_xs.png`,
        [LayerContextEnum.Acdelco]: `${environment.get('IMAGES_HOST')}/aftersales/pin_acdelco.png`,
        [`${LayerContextEnum.Acdelco}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_cyan.png`,
        [`${LayerContextEnum.Acdelco}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_cyan_xs.png`,
        [LayerContextEnum.Accioly]: `${environment.get('IMAGES_HOST')}/aftersales/pin_acdelco_accioly.png`,
        [`${LayerContextEnum.Accioly}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_purple.png`,
        [`${LayerContextEnum.Accioly}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_purple_xs.png`,
        [LayerContextEnum.MotoWorkShop]: `${environment.get('IMAGES_HOST')}/motos/pin_workshop.png`,
        [`${LayerContextEnum.MotoWorkShop}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_blue.png`,
        [`${LayerContextEnum.MotoWorkShop}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_blue_xs.png`,
        [LayerContextEnum.MotoParts]: `${environment.get('IMAGES_HOST')}/motos/pin_parts.png`,
        [`${LayerContextEnum.MotoParts}_sm`]: `${environment.get('IMAGES_HOST')}/pin_circle_red.png`,
        [`${LayerContextEnum.MotoParts}_xs`]: `${environment.get('IMAGES_HOST')}/pin_circle_red_xs.png`,
    }

    static getLegendName = {
        [LayerContextEnum.Workshop]: () => translate('Common.Workshop'),
        [LayerContextEnum.AutoParts]: () => translate('Common.AutoParts'),
        [LayerContextEnum.Accioly]: () => translate('Common.Accioly'),
        [LayerContextEnum.Acdelco]: () => translate('Common.Acdelco'),
        [LayerContextEnum.MotoParts]: () => translate('Common.MotoParts'),
        [LayerContextEnum.MotoWorkShop]: () => translate('Common.Workshop')
    }

    static getPropertiesByLayerContext(features, layerContext) {
        return features
            .filter(x => x.feature.properties.LayerContext === layerContext)
            .map(x => ({
                ...x,
                socialReason: WorkshopPartLayer.getTitle(x.feature.properties.Parameters),
                storeName: WorkshopPartLayer.getTitleStoreName(x.feature.properties.Parameters),
            }));
    }

    static getTitleStoreName(parameters) {
        const storeName = parameters.filter(y => y.Key === WorkshopPartParameter.StoreName);

        if (storeName.length) {
            return storeName[0].Value.toUpperCase();
        }

        return '';
    }

    static getTitle(parameters) {
        const fantasyName = parameters.filter(y => y.Key === WorkshopPartParameter.FantasyName);
        const socialReason = parameters.filter(y => y.Key === WorkshopPartParameter.CompanyName);

        const fn = fantasyName.length > 0 ? fantasyName[0].Value : null;
        const sr = socialReason.length > 0 ? socialReason[0].Value : null;

        return `${fn || sr}`;
    };

}

export default WorkshopPartLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/WorkshopPartLayer.js